import { faBuildingUser, faClock, faEye, faFlag, faTriangleExclamation, faUser } from '@fortawesome/free-solid-svg-icons';
import { ladderLeft, liveLadder, prioOf, stampOf, typeIcon, typeLabel } from '../../utils/actions';
import { callerLabel } from '../../utils/callLog';
import { Av, Btn, Chip, I, LockBtn, cx } from './ui';

const PRIO = {
  urgent: ['urgent', faTriangleExclamation, 'Urgent'],
  high: ['high', faFlag, 'High'],
  normal: ['', faFlag, 'Normal']
};

export function PrioChip({ p, ac }) {
  const [cls, icon, text] = PRIO[p] || PRIO.normal;
  return (
    <span className={cx(ac ? 'ac-pri' : 'aa2-pri', cls)}>
      <I icon={icon} />
      {text}
    </span>
  );
}

export function ageOf(x, now) {
  if (x.lane === 'done') return null;
  if (liveLadder(x)) return ['red', `Escalating · ${ladderLeft(x, now) || ''} left`];
  if (x.aging?.text) return [x.aging.state === 'red' ? 'red' : x.aging.state === 'amber' ? 'amber' : '', x.aging.text];
  return null;
}

function shortAge(text) {
  if (/^Overdue/.test(text)) return 'Overdue';
  if (/^Due in/.test(text)) return text.replace(/ min$/, 'm').replace(' h ', 'h ');
  return text.length > 14 ? `${text.slice(0, 13)}…` : text;
}

const Age = ({ age, short }) => (
  <span className={cx('aa2-age', age[0])} title={age[1]} aria-label={age[1]}>
    <I icon={faClock} />
    {short ? shortAge(age[1]) : age[1]}
  </span>
);

export function primaryOf(x, why, run) {
  if (x.lane === 'done') return null;
  const label = x.lane === 'review' ? 'Looks right' : x.kind === 'batch' ? 'Open batch' : x.approve?.label;
  if (!label) return null;
  if (why) return <LockBtn label={label} why={why} sm />;
  return (
    <Btn
      kind="primary"
      sm
      onClick={(e) => {
        e.stopPropagation();
        run(x);
      }}
    >
      {label}
    </Btn>
  );
}

const OwnerAv = ({ who }) =>
  who === 'Unassigned' ? (
    <span className="m-av lt sm" title="Unassigned">
      <I icon={faUser} />
    </span>
  ) : (
    <span title={who}>
      <Av name={who} cls="sm" />
    </span>
  );

export default function Card({ x, size, tz, now, agent, owner, why, onOpen, onPrimary }) {
  const p = prioOf(x);
  const age = ageOf(x, now);
  const when = x.dueAt ? `Due ${stampOf(x.dueAt, tz, now)}` : stampOf(x.arrivedAt, tz, now);
  const name = callerLabel(x) || agent;
  const short =
    owner === 'Unassigned' || owner === 'Agent Zero team'
      ? owner
      : `${owner.split(' ')[0]} ${(owner.split(' ')[1] || '').charAt(0)}${owner.includes(' ') ? '.' : ''}`;
  const type = (
    <span className="aa2-type" title={typeLabel(x.type)}>
      <I icon={typeIcon(x.type)} />
      {typeLabel(x.type)}
    </span>
  );
  const stage =
    x.lane === 'review' ? (
      <Chip fam="purple" icon={faEye}>
        Needs review
      </Chip>
    ) : x.lane === 'office' ? (
      <Chip fam="info" icon={faBuildingUser}>
        Escalations
      </Chip>
    ) : null;
  return (
    <article
      className={cx('aa2-card', size === 'compact' ? 'cmp' : 'std', p === 'urgent' && 'urgent', p === 'high' && 'high')}
      draggable
      tabIndex={0}
      aria-label={`${p}: ${x.title}`}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', x._id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={() => onOpen(x)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(x)}
    >
      {size === 'compact' ? (
        <>
          <div className="aa2-title" title={x.title}>{x.title}</div>
          <div className="aa2-crow">
            <PrioChip p={p} />
            {age ? <Age age={age} short /> : type}
            <div className="grow" />
            <OwnerAv who={owner} />
          </div>
        </>
      ) : (
        <>
          <div className="aa2-top">
            <PrioChip p={p} />
            {type}
            <div className="grow" />
          </div>
          <div className="aa2-title" title={x.title}>{x.title}</div>
          <div className="aa2-who">
            {age ? (
              <Age age={age} />
            ) : (
              stage || (
                <>
                  <span>{name}</span>
                  <span className="aa2-dot">·</span>
                  <span className="aa2-when">{when}</span>
                </>
              )
            )}
          </div>
          <div className="aa2-foot">
            {primaryOf(x, why, onPrimary)}
            <div className="grow" />
            <div className="aa2-owner">
              <OwnerAv who={owner} />
              <span>{short}</span>
            </div>
          </div>
        </>
      )}
    </article>
  );
}
